import * as React from "react";
import { useCallback } from "react";

import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import type { Backtest } from "../../../../types/backtests";
import type { Report } from "../../../../types/reports";
import type { Trade } from "../../../../types/trade";
import type { CohortListFilter, CohortPreviewStats } from "../../../../utils/cohortTradesResolve";
import {
  computeCohortPreviewStats,
  filterReportTradesForCohortPreview,
} from "../../../../utils/cohortTradesResolve";
import {
  EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP,
} from "../../../../utils/executionCandlesParams";
import { reportDetailSectionCardProps } from "./ReportsCardChrome";
import { ReportTradeReviewPanel } from "../ReportTradeReviewPanel";
import type { MarketCohortPreviewState } from "../useMarketCohortPreview";
import { MarketCohortPreviewSummary } from "./MarketCohortPreviewSummary";

function sideStatsEqual(
  a: CohortPreviewStats["longs"],
  b: CohortPreviewStats["longs"],
): boolean {
  return (
    a.count === b.count &&
    a.wins === b.wins &&
    a.losses === b.losses &&
    a.breakeven === b.breakeven &&
    a.winRate === b.winRate &&
    a.netPnl === b.netPnl
  );
}

function cohortPreviewStatsEqual(
  a: CohortPreviewStats | null | undefined,
  b: CohortPreviewStats | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a == null && b == null;
  return (
    a.inCohort === b.inCohort &&
    a.resolvedCount === b.resolvedCount &&
    a.wins === b.wins &&
    a.losses === b.losses &&
    a.breakeven === b.breakeven &&
    a.winRate === b.winRate &&
    a.netPnl === b.netPnl &&
    sideStatsEqual(a.longs, b.longs) &&
    sideStatsEqual(a.shorts, b.shorts)
  );
}

export function upsertCohortOverlayStats(
  prev: Record<string, CohortPreviewStats | null>,
  rowKey: string,
  nextStats: CohortPreviewStats | null,
): Record<string, CohortPreviewStats | null> {
  if (cohortPreviewStatsEqual(prev[rowKey], nextStats)) return prev;
  return { ...prev, [rowKey]: nextStats };
}

export type ReportMarketCohortPreviewCardProps = {
  preview: MarketCohortPreviewState;
  tradesForReview: Trade[];
  tradesByIdForCohortStats: Map<string, Trade>;
  overlayStats: CohortPreviewStats | null | undefined;
  onUpsertOverlayStats: (rowKey: string, stats: CohortPreviewStats | null) => void;
  onListFilterChange: (rowKey: string, filter: CohortListFilter) => void;
  onClear: (rowKey: string) => void;
  tradesLoading: boolean;
  tradesError: string | null;
  report: Report;
  brokerAccountCode: string | null;
  reportTimezone: string | null | undefined;
  candleTimeframe: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | null | undefined;
  recordedTimezoneLabel: string | undefined;
  asOfEndMs: number | null | undefined;
  requireInstrumentSetup: boolean;
  /** Demo Entry Analysis — Market preview uses demo cohort + execution-candles. */
  demoJobId?: string;
};

export function ReportMarketCohortPreviewCard(
  props: ReportMarketCohortPreviewCardProps,
): React.ReactElement {
  const { preview } = props;

  const onCohortPreviewStats = useCallback(
    (nextStats: CohortPreviewStats | null) => {
      props.onUpsertOverlayStats(preview.rowKey, nextStats);
    },
    [preview.rowKey, props.onUpsertOverlayStats],
  );

  const onListFilterChange = useCallback(
    (filter: CohortListFilter) => {
      props.onListFilterChange(preview.rowKey, filter);
    },
    [preview.rowKey, props.onListFilterChange],
  );

  const onClear = useCallback(() => {
    props.onClear(preview.rowKey);
  }, [preview.rowKey, props.onClear]);

  const statsFromTrades = React.useMemo(() => {
    const cohortIds = preview.tradeIds.map((id) => id.trim().toLowerCase()).filter(Boolean);
    if (preview.listFilter === "winners" || preview.listFilter === "losers") {
      const filtered = filterReportTradesForCohortPreview(
        Array.from(props.tradesByIdForCohortStats.values()),
        preview.listFilter,
        preview.tradeIds,
      );
      const filteredIds = filtered
        .map((t) => String(t.id ?? "").trim().toLowerCase())
        .filter(Boolean);
      return computeCohortPreviewStats(filteredIds, props.tradesByIdForCohortStats);
    }
    return computeCohortPreviewStats(cohortIds, props.tradesByIdForCohortStats);
  }, [preview.listFilter, preview.tradeIds, props.tradesByIdForCohortStats]);
  const stats = props.overlayStats ?? statsFromTrades;

  /** Full cohort ↔ report trade match count (ignore Winners/Losers tab). */
  const matchedInReportCount = React.useMemo(() => {
    let matched = 0;
    for (const rawId of preview.tradeIds) {
      const id = rawId.trim().toLowerCase();
      if (id && props.tradesByIdForCohortStats.has(id)) matched += 1;
    }
    return matched;
  }, [preview.tradeIds, props.tradesByIdForCohortStats]);

  const previewTrades = React.useMemo(() => {
    if (preview.status !== "ready") return props.tradesForReview;
    return filterReportTradesForCohortPreview(
      props.tradesForReview,
      preview.listFilter,
      preview.tradeIds,
    );
  }, [preview.listFilter, preview.status, preview.tradeIds, props.tradesForReview]);

  return (
    <Card
      {...reportDetailSectionCardProps}
      mt={6}
      data-market-cohort-preview={preview.rowKey}
    >
      <CardBody display="flex" flexDirection="column" p={0} w="full">
        <MarketCohortPreviewSummary
          label={preview.label}
          stats={stats}
          reportTradeCount={props.tradesForReview.length}
          expectedSampleSize={preview.expectedSampleSize}
          resolvedTotal={preview.total}
          matchedInReportCount={matchedInReportCount}
          listFilter={preview.listFilter}
          onListFilterChange={onListFilterChange}
          onClear={onClear}
          loading={preview.status === "loading"}
          error={preview.status === "error" ? preview.error : undefined}
          isEmpty={preview.status === "empty"}
        />
        {preview.status === "ready" ? (
          <ReportTradeReviewPanel
            brokerAccountCode={props.brokerAccountCode}
            trades={previewTrades}
            tradesLoading={props.tradesLoading}
            tradesError={props.tradesError}
            reportId={props.report.id}
            // Large market-condition cohorts must use report_id — never dump thousands of IDs into trade_ids.
            // Demo has no production report cohort — always send exact trade_ids via demo job endpoint.
            reportIdForCohort={
              props.demoJobId
                ? undefined
                : preview.tradeIds.length > EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP
                  ? props.report.id
                  : undefined
            }
            cohortIntent={
              props.demoJobId
                ? "policy_exact"
                : preview.tradeIds.length > EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP
                  ? "report"
                  : "policy_exact"
            }
            reportTimeframe={props.report.timeframe}
            reportStartDate={props.report.start_date}
            reportEndDate={props.report.end_date}
            reportTimezone={props.reportTimezone}
            cohortPeriod={props.report.cohort_period}
            inputSnapshot={props.report.input_snapshot}
            asOfEndMs={props.asOfEndMs}
            defaultCandleTimeframe={props.candleTimeframe ?? undefined}
            recordedTimezoneLabel={props.recordedTimezoneLabel}
            linkedBacktests={EMPTY_BACKTESTS}
            overlayBacktestId=""
            onOverlayBacktestIdChange={noopString}
            overlayEnabled={false}
            onOverlayEnabledChange={noopBoolean}
            overlayTradeIdsLoading={false}
            overlayTradeIdsError={null}
            overlayTradeIds={null}
            policyOverlay={null}
            hideOverlayControls
            exactTradeIds={
              props.demoJobId
                ? preview.tradeIds
                : preview.tradeIds.length > EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP
                  ? undefined
                  : preview.tradeIds
            }
            demoJobId={props.demoJobId}
            forceChartSymbol={props.report.symbol || undefined}
            cohortHighlightIds={preview.tradeIds}
            cohortPreviewMode
            cohortListFilter={preview.listFilter}
            onCohortPreviewStats={onCohortPreviewStats}
            requireInstrumentSetup={props.requireInstrumentSetup}
            uiPersistenceKey={`market-cohort-preview:${preview.rowKey}`}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

const EMPTY_BACKTESTS: Backtest[] = [];
function noopString(_value: string): void {}
function noopBoolean(_value: boolean): void {}
